import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Panel, Tab, Tabs } from 'react-bootstrap';
import { navigate } from 'raviger';
import _ from 'lodash';

import { breadcrumbAddRoute, breadcrumbRemoveRoute } from '../../../store/actions/breadcrumb';

import withAuthentication from '../../../hoc/withAuthentication';
import withLayout from '../../../hoc/withLayout';
import Permission from '../../../components/Permission/Permission';

import { useAlertStore } from '../../../store/providers/Alert';
import { useMarketplaceVendorStore } from '../../../store/providers/MarketplaceVendor';

import Spinner from '../../../components/Spinner/Spinner';

import * as constants from '../../../constants';
import './MarketplaceVendor.css';

const SideMenu = React.lazy(() => import('../../../components/SideMenu/SideMenu'));
const PageHeader = React.lazy(() => import('../../../components/PageHeader/PageHeader'));
const MarketplaceVendorForm = React.lazy(() => import('../../../components/Forms/MarketplaceVendorForm/MarketplaceVendorForm'));
const MarketplaceDealTab = React.lazy(() => import('../../../components/Tabs/MarketplaceDealTab/MarketplaceDealTab'));
const MarketplaceAssetTab = React.lazy(() => import('../../../components/Tabs/MarketplaceAssetTab/MarketplaceAssetTab'));
const MarketplaceFranchiseTab = React.lazy(() => import('../../../components/Tabs/MarketplaceFranchiseTab/MarketplaceFranchiseTab'));

function MarketplaceVendor(props) {
    // Redux state
    const dispatch = useDispatch();

    // Local state
    const [selectedTab, setSelectedTab] = useState();
    const [isMounted, setIsMounted] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const [title, setTitle] = useState();

    const { addAlert } = useAlertStore();
    const { selectRecord, newRecord, data, clearData } = useMarketplaceVendorStore();

    const pagePermission = constants.PERMISSION_MARKETPLACE_VENDORS;
    const [hasPermission, setHasPermission] = useState(false);
    const [, setCanEdit] = useState(false);


    const tabSelectedHandler = (tabKey) => {
        setSelectedTab(tabKey);
    };

    const submittedHandler = () => {
        navigate('/marketplaceVendors');
    };

    const cancelledHandler = () => {
        navigate('/marketplaceVendors');
    };

    useEffect(() => {
        if (!_.isEmpty(data)) {
            if (data.status === 'fail' || data.status === 'error') {
                addAlert({ key: constants.ALERT_SERVER_ERROR, text: <strong>{data.body}</strong>, type: constants.ALERT_TYPE_DANGER });
                clearData();
            }

            if (!isLoaded && isMounted && (data.scope === 'select' || data.scope === 'new') && data.status === 'success') {
                setIsLoaded(true);
                if (data.body && data.body.id) {
                    dispatch(breadcrumbAddRoute({
                        key: 'edit-marketplace',
                        parent: 'Edit Marketplace Vendor',
                        text: data.body.name,
                        path: {
                            event: () => setSelectedTab('general')
                        }
                    }));
                }
            }
        }

        setIsBusy(!_.isEmpty(data) && (data.status === 'processing' && (data.scope === 'select' || data.scope === 'new')));
    }, [props, data, addAlert, clearData, isLoaded, isMounted, dispatch, setSelectedTab]);

    useEffect(() => {
        if (!hasPermission) { return; }
        if (isMounted) { return; }

        dispatch(breadcrumbAddRoute({ key: 'marketplace', parent: 'Marketplace', text: 'Marketplace Vendors', path: '/marketplace/vendors' }));

        if (!_.isEmpty(props.queryParams) && !_.isEmpty(props.queryParams.marketplaceVendorId)) {
            selectRecord({ marketplaceVendorId: props.queryParams.marketplaceVendorId });
            setTitle(`Edit Marketplace Vendor (Id: ${props.queryParams.marketplaceVendorId})`);
        } else {
            dispatch(breadcrumbAddRoute({
                key: 'new-marketplace',
                text: 'New Marketplace Vendor',
                path: {
                    event: () => setSelectedTab('general')
                }
            }));
            newRecord();
            setTitle(`New Marketplace Vendor`);
        }

        setIsMounted(true);
    }, [isMounted, hasPermission, data, props, newRecord, selectRecord, dispatch]);

    useEffect(() => {
        return () => {
            dispatch(breadcrumbRemoveRoute());
        };
    }, [dispatch]);

    return (
        <>
            <SideMenu activeKey="marketplace" />
            <div className="content-body">
                <Permission showDenied={true} pagePermission={pagePermission} hasPermission={hasPermission} setHasPermission={setHasPermission} setCanEdit={setCanEdit} />
                {hasPermission && isMounted &&
                    <div style={{ paddingRight: '15px' }}>
                        <PageHeader title={title} />
                        {
                            !isLoaded && <Spinner text="Loading Marketplace Vendor..." />
                        }
                        {
                            isLoaded &&
                            <Panel>
                                <Panel.Body>
                                    <Tabs bsStyle="pills"
                                        defaultActiveKey="general" id="vendor"
                                        animation={true}
                                        activeKey={selectedTab}
                                        onSelect={tabSelectedHandler}>
                                        <Tab eventKey="general" title="General">
                                            <MarketplaceVendorForm
                                                cancelled={() => cancelledHandler()}
                                                submitted={() => submittedHandler()} />
                                        </Tab>
                                        <Tab eventKey="deals" title="Deals" disabled={isBusy || !data.body}>
                                            {
                                                (!isBusy && selectedTab === "deals") &&
                                                <MarketplaceDealTab dataItem={data.body} />
                                            }
                                        </Tab>
                                        <Tab eventKey="assets" title="Assets" disabled={isBusy || !data.body}>
                                            {
                                                (!isBusy && selectedTab === "assets") && <MarketplaceAssetTab dataItem={data.body} />
                                            }
                                        </Tab>
                                        <Tab eventKey="franchises" title="Instances" disabled={isBusy || !data.body}>
                                            {
                                                (!isBusy && selectedTab === "franchises") && <MarketplaceFranchiseTab dataItem={data.body} />
                                            }
                                        </Tab>
                                    </Tabs>
                                </Panel.Body>
                            </Panel>
                        }
                    </div>
                }
            </div>
        </>
    );
}

export default withAuthentication(withLayout(MarketplaceVendor));
